import {
  CreditCardIcon,
  LandmarkIcon,
  type LucideIcon,
  ReceiptTextIcon,
  TrendingUpIcon,
} from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useProject } from '@/modules/projects/hooks'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'

const dialogId = 'supported-files'
const ACKNOWLEDGE_CHECKBOX_ID = 'supported-files-acknowledged'

interface FileKind {
  label: string
  icon: LucideIcon
}

const BASE_FILE_KINDS: FileKind[] = [
  { label: 'Bank statements', icon: LandmarkIcon },
  { label: 'Credit card statements', icon: CreditCardIcon },
  { label: 'Check images', icon: ReceiptTextIcon },
]

const BROKERAGE_FILE_KIND: FileKind = {
  label: 'Brokerage statements',
  icon: TrendingUpIcon,
}

const supportedFileKinds = (isBrokerageEnabled: boolean) =>
  isBrokerageEnabled
    ? [...BASE_FILE_KINDS, BROKERAGE_FILE_KIND]
    : BASE_FILE_KINDS

interface SupportedFilesDialogData {
  onAcknowledge: () => void
}

export const SupportedFilesDialog = () => {
  const open = useDialogIsOpen(dialogId)
  const { closeDialog } = useDialogActions()

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && closeDialog()}>
      <DialogContent className="max-w-md">
        <SupportedFilesForm />
      </DialogContent>
    </Dialog>
  )
}

// Lives inside DialogContent so the checkbox starts unchecked on every open.
const SupportedFilesForm = () => {
  const [acknowledged, setAcknowledged] = useState(false)
  const { closeDialog } = useDialogActions()
  const data = useDialogData() as SupportedFilesDialogData | undefined
  const fileKinds = supportedFileKinds(
    Boolean(useProject()?.isBrokerageEnabled),
  )

  const handleProcess = () => {
    closeDialog(true)
    data?.onAcknowledge()
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Before you process</DialogTitle>
        <DialogDescription>
          CounselPro only reads these kinds of files.
        </DialogDescription>
      </DialogHeader>
      <ul className="flex flex-col gap-3 py-1">
        {fileKinds.map(({ label, icon: Icon }) => (
          <li
            key={label}
            className="flex items-center gap-3 text-sm font-medium"
          >
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            {label}
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">
        Other types of files will not process correctly, and will count against
        your page credits.
      </p>
      <label
        htmlFor={ACKNOWLEDGE_CHECKBOX_ID}
        className="flex cursor-pointer items-center gap-3 pt-2 text-sm"
      >
        <Checkbox
          id={ACKNOWLEDGE_CHECKBOX_ID}
          checked={acknowledged}
          onCheckedChange={setAcknowledged}
        />
        I understand that only these file types are supported.
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <Button theme="outline" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button disabled={!acknowledged} onClick={handleProcess}>
          Process
        </Button>
      </div>
    </>
  )
}

SupportedFilesDialog.id = dialogId
